import { getSubdomainFranchise } from '../utils/get-subdomain';
import { httpInterceptor } from './http.interceptor';

export interface PreCadastro {
  id: number;
  fullName: string;
  cpfDocument: string;
  rgDocument?: string;
  phone: string;
  email: string;
  instagram?: string;
  cityState: string;
  neighborhood?: string;
  address?: string;
  cep?: string;
  residence: string;
  residenceTime?: string;
  job: string;
  income: string;
  // Dados Cívicos
  scholarLevel?: string;
  dateOfBirth?: string;
  maritalStatus?: string;
  nationality?: string;
  // Restrições
  cpfRestrictions?: string;
  avalist?: string;
  creditCard?: string;
  // Parentalidade
  mothersName?: string;
  children?: string;
  // Motivação e Objetivos
  salesMotivation?: string;
  profitObjective?: string;
  doubt?: string;
  // Referências
  referenceNameOne?: string;
  referencePhoneOne?: string;
  referenceNameTwo?: string;
  referencePhoneTwo?: string;
  referenceComercial?: string;
  referenceComercialPhone?: string;
  // Indicador
  indicatedCode?: string;
  indicador_nome?: string;
  indicador_role?: string;
  // Interesse
  grauInteresse?: string;
  aprenderAplicativo?: string;
  usoTecnologia?: string;
  // Relacionamentos
  colaborador_responsavel?: string;
  coordenadora_id: number | null;
  colaborador_id: number | null;
  situacao: 'pendente' | 'analisando' | 'aprovado' | 'aguardando_assinaturas' | 'concluido' | 'reprovado' | 'deletado';
  createdAt: string;
  updatedAt: string;

  acordoContrato: string;
  ambassadorReason: string;
  residenceProof: string;
  veiculo: string;
  
  disponivelEntrevistaOnline?: string;
  disponivelTreinamentoVendas?: string;

  // Dados Serasa
  serasaScore?: number;
  serasaRestricoes?: number;
  serasaChancePagamento?: number;
}

export interface Kit {
  id: number;
  name: string;
  description: string;
  price: number;
  imageOnePath: string | null;
  imageTwoPath: string | null;
  imageThreePath: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Document {
  id: string;
  path: string;
  fileUploaded: string;
  createdAt: string;
  updatedAt: string;
}

export interface Attachment {
  id: number;
  preRegisterId: number;
  name: string;
  description: string;
  fileUploaded: string;
  base64: string | null;
  fileType: string | null;
  attachmentType: string;
  path: string | null;
  url: string | null;
  situacao: string;
  createdAt: string;
}

export interface ToDo {
  id: number;
  title: string;
  description: string | null;
  situacao: 'pendente' | 'concluido';
  ordem: number;
  concludedAt: string | null;
  concludedBy: string | null;
  createdAt: string;
}

export interface Note {
  id: number;
  title: string;
  message: string;
  color: string;
  autor: string;
  createdAt: string;
}

export interface WhatsAppQueue {
  id: number;
  preRegisterId: number;
  recipientName: string;
  phoneNumber: string;
  senderName: string;
  status: 'pendente' | 'enviado' | 'falha' | 'cancelado';
  attempts: number | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  processedAt: string | null;
}

interface PaginationResponse<T> {
  data: T[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
  pagination?: {
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  bigNumbers?: {
    totalPendente: number;
    totalAnalisando: number;
    totalAprovadas: number;
    totalReprovadas: number;
    totalAguardandoAssinaturas: number;
    totalConcluidos: number;
  };
}

/**
 * Detecta se um anexo de contrato é legado D4Sign (anterior à migração para ClickSign).
 * Critério: contratos ClickSign têm `path === 'clicksign'`; contratos D4Sign têm `path`
 * com URL absoluta D4Sign ou `null`.
 */
export const isLegacyD4SignAttachment = (attachment: Attachment): boolean => {
  return attachment.path !== 'clicksign';
};

class ManagementService {
  private subdomain: string;

  constructor() {
    this.subdomain = getSubdomainFranchise();
  }

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      'x-api-key': btoa(this.subdomain),
    };
  }

  /**
   * Lista pré-cadastros com paginação e filtros (server-side)
   */
  async getPreCadastrosPaginated(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
      dateStart?: string;
      dateEnd?: string;
      coordenadoraId?: number;
      colaborador?: string;
    }
  ): Promise<{ data: PreCadastro[]; total: number; page: number; limit: number; bigNumbers?: { totalPendente: number; totalAnalisando: number; totalAprovadas: number; totalReprovadas: number; totalAguardandoAssinaturas: number; totalConcluidos: number } }> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set('page', String(params.page || 1));
      queryParams.set('size', String(params.limit || 50));
      queryParams.set('sort', 'createdAt');
      queryParams.set('order', 'DESC');
      if (params.search) queryParams.set('search', params.search);
      if (params.status) queryParams.set('status', params.status);
      if (params.dateStart) queryParams.set('dateStart', params.dateStart);
      if (params.dateEnd) queryParams.set('dateEnd', params.dateEnd);
      if (params.coordenadoraId) queryParams.set('coordenadoraId', String(params.coordenadoraId));
      if (params.colaborador) queryParams.set('colaborador', params.colaborador);

      const response = await httpInterceptor.get(
        `/user/pre-registers?${queryParams.toString()}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar pré-cadastros');
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar pré-cadastros:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getPreCadastrosPaginated instead
   * Lista pré-cadastros com paginação e filtros
   */
  async getPreCadastros(
    page: number = 1,
    size: number = 20,
    sort: string = 'createdAt',
    order: string = 'DESC',
    search: string = '',
    status: string = '',
    periodInicio: string = '',
    periodFim: string = '',
    coordenadoraId: number | null = null,
    colaboradorResponsavel: string = ''
  ): Promise<PaginationResponse<PreCadastro>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sort,
        order,
      });

      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (periodInicio) params.append('periodInicio', periodInicio);
      if (periodFim) params.append('periodFim', periodFim);
      if (coordenadoraId) params.append('coordenadoraId', coordenadoraId.toString());
      if (colaboradorResponsavel) params.append('colaboradorResponsavel', colaboradorResponsavel);

      const response = await httpInterceptor.get(
        `/user/pre-registers?${params.toString()}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar pré-cadastros');
      }

      // A API retorna a estrutura correta, então apenas processamos
      const paginationData: PaginationResponse<PreCadastro> = {
        data: data.data || [],
        total: data.pagination?.totalItems || 0,
        page: data.pagination?.page || page,
        size: data.pagination?.size || size,
        totalPages: data.pagination?.totalPages || 0,
        pagination: data.pagination,
        bigNumbers: data.bigNumbers,
      };
      return paginationData;
    } catch (error) {
      console.error('Erro ao buscar pré-cadastros:', error);
      throw error;
    }
  }

  /**
   * Obtém detalhes de um pré-cadastro específico
   */
  async getPreCadastroById(id: number): Promise<PreCadastro> {
    try {
      const response = await httpInterceptor.get(
        `/user/pre-register/${id}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar pré-cadastro');
      }

      return data.data;
    } catch (error) {
      console.error('Erro ao buscar pré-cadastro:', error);
      throw error;
    }
  }

  /**
   * Inicia análise de pré-cadastro
   */
  async startAnalysisPreCadastro(
    preCadastroId: number,
    collaborator: string
  ): Promise<any> {
    try {
      const response = await httpInterceptor.patch(
        `/user/start-analysis/${preCadastroId}`,
        { collaborator },
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao iniciar análise');
      }

      return data;
    } catch (error) {
      console.error('Erro ao iniciar análise:', error);
      throw error;
    }
  }

  /**
   * Aprova pré-cadastro
   */
  async approveAnalysisPreCadastro(
    preCadastroId: number,
    kitId: string,
    coordenadoraId: number,
    dontDoContract: boolean = false
  ): Promise<any> {
    try {
      const response = await httpInterceptor.post(
        `/pre-register/approve/${preCadastroId}`,
        {
          kitId: Number(kitId),
          coordId: coordenadoraId,
          dontDoContract: dontDoContract,
        },
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao aprovar pré-cadastro');
      }

      return data;
    } catch (error) {
      console.error('Erro ao aprovar pré-cadastro:', error);
      throw error;
    }
  }

  /**
   * Reprova pré-cadastro
   */
  async reproveAnalysisPreCadastro(
    preCadastroId: number,
    reason: string
  ): Promise<any> {
    try {
      const response = await httpInterceptor.post(
        `/pre-register/reprove/${preCadastroId}`,
        { reason },
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao reprovar pré-cadastro');
      }

      return data;
    } catch (error) {
      console.error('Erro ao reprovar pré-cadastro:', error);
      throw error;
    }
  }

  /**
   * Lista anexos de um pré-cadastro
   */
  async getAttachments(preRegisterId: number): Promise<Attachment[]> {
    try {
      const response = await httpInterceptor.get(
        `/pre-register/attachments/${preRegisterId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar anexos');
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar anexos:', error);
      throw error;
    }
  }

  /**
   * Cria um anexo para um pré-cadastro
   */
  async createAttachment(
    preRegisterId: number,
    attachmentData: any
  ): Promise<any> {
    try {
      const response = await httpInterceptor.post(
        `/pre-register/attachments/${preRegisterId}`,
        attachmentData,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao criar anexo');
      }

      return data;
    } catch (error) {
      console.error('Erro ao criar anexo:', error);
      throw error;
    }
  }

  /**
   * Deleta um anexo
   */
  async deleteAttachment(attachmentId: number): Promise<any> {
    try {
      const response = await httpInterceptor.delete(
        `/pre-register/attachments/${attachmentId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao deletar anexo');
      }

      return data;
    } catch (error) {
      console.error('Erro ao deletar anexo:', error);
      throw error;
    }
  }

  /**
   * Lista kits
   */
  async getKits(): Promise<Kit[]> {
    try {
      const response = await httpInterceptor.get(
        '/pre-register/kits',
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar kits');
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar kits:', error);
      throw error;
    }
  }

  /**
   * Atualiza um kit
   */
  async updateKit(kitId: number, kitData: any): Promise<any> {
    try {
      const response = await httpInterceptor.patch(
        `/pre-register/kits/${kitId}`,
        kitData,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao atualizar kit');
      }

      return data;
    } catch (error) {
      console.error('Erro ao atualizar kit:', error);
      throw error;
    }
  }

  /**
   * Obtém documentos de assinatura
   */
  async getDocuments(): Promise<Document> {
    try {
      const response = await httpInterceptor.get(
        '/pre-register/documents',
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar documentos');
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      throw error;
    }
  }

  /**
   * Atualiza documento de assinatura
   */
  async updateContractDocSign(
    id: string,
    fileSign: { fileUploaded: string | null; base64: string | null; fileType: string | null }
  ): Promise<any> {
    try {
      const response = await httpInterceptor.patch(
        `/pre-register/documents/${id}`,
        {
          fileUploaded: fileSign.fileUploaded,
          base64: fileSign.base64,
          fileType: fileSign.fileType,
        },
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao atualizar documento');
      }

      return data;
    } catch (error) {
      console.error('Erro ao atualizar documento:', error);
      throw error;
    }
  }

  /**
   * Reenvia email de assinatura
   */
  async resendEmailSign(attachmentId: number): Promise<any> {
    try {
      const response = await httpInterceptor.post(
        `/pre-register/attachments/resend-contract/${attachmentId}`,
        {},
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao reenviar email');
      }

      return data;
    } catch (error) {
      console.error('Erro ao reenviar email:', error);
      throw error;
    }
  }

  /**
   * Reenvia email de assinatura — versão legada D4Sign.
   * Usar quando isLegacyD4SignAttachment(attachment) === true.
   */
  async resendEmailSignLegacy(attachmentId: number): Promise<any> {
    try {
      const response = await httpInterceptor.post(
        `/pre-register/attachments/resend-contract-legacy/${attachmentId}`,
        {},
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao reenviar email');
      }

      return data;
    } catch (error) {
      console.error('Erro ao reenviar email (legacy):', error);
      throw error;
    }
  }

  /**
   * Muda kit de anexo
   */
  async changeKit(attachmentId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(
        `/pre-register/attachments/change-kit/${attachmentId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao trocar kit');
      }

      return data;
    } catch (error) {
      console.error('Erro ao trocar kit:', error);
      throw error;
    }
  }

  /**
   * Verifica contrato assinado
   */
  async checkContractSigned(attachmentId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(
        `/pre-register/attachments/check-contract/${attachmentId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao verificar contrato');
      }

      return data;
    } catch (error) {
      console.error('Erro ao verificar contrato:', error);
      throw error;
    }
  }

  /**
   * Verifica contrato assinado — versão legada D4Sign.
   * Usar quando isLegacyD4SignAttachment(attachment) === true.
   */
  async checkContractSignedLegacy(attachmentId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(
        `/pre-register/attachments/check-contract-legacy/${attachmentId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao verificar contrato');
      }

      return data;
    } catch (error) {
      console.error('Erro ao verificar contrato (legacy):', error);
      throw error;
    }
  }

  /**
   * Obtém URL de download do contrato ClickSign (válida por 5 minutos)
   */
  async getDownloadUrl(attachment: any): Promise<string> {
    try {
      const response = await httpInterceptor.post(
        `/pre-register/attachments/download-contract/${attachment.url}`,
        {},
        { headers: this.getHeaders() }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao obter URL de download');
      }

      return data.data?.url;
    } catch (error) {
      console.error('Erro ao obter URL de download:', error);
      throw error;
    }
  }

  /**
   * Aprova assinatura
   */
  async approveSignature(attachmentId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(
        `/pre-register/contract/approve-signature/${attachmentId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao aprovar assinatura');
      }

      return data;
    } catch (error) {
      console.error('Erro ao aprovar assinatura:', error);
      throw error;
    }
  }

  /**
   * Obtém tarefas (check list) de um pré-cadastro
   */
  async getTodos(preRegisterId: number): Promise<ToDo[]> {
    try {
      const response = await httpInterceptor.get(
        `/pre-register/tasks/${preRegisterId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar tarefas');
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar tarefas:', error);
      throw error;
    }
  }

  /**
   * Atualiza status de uma tarefa
   */
  async updateTodo(todoId: number, situacao: 'pendente' | 'concluido'): Promise<any> {
    try {
      const response = await httpInterceptor.patch(
        `/pre-register/tasks/${todoId}`,
        { situacao },
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao atualizar tarefa');
      }

      return data;
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
      throw error;
    }
  }

  /**
   * Obtém notas (comentários) de um pré-cadastro
   */
  async getNotes(preRegisterId: number): Promise<Note[]> {
    try {
      const response = await httpInterceptor.get(
        `/pre-register/comments/${preRegisterId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar notas');
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar notas:', error);
      throw error;
    }
  }

  /**
   * Cria uma nova nota
   */
  async createNote(preRegisterId: number, note: { title: string; message: string; color: string }): Promise<Note> {
    try {
      const response = await httpInterceptor.post(
        `/pre-register/comments/${preRegisterId}`,
        note,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao criar nota');
      }

      return data;
    } catch (error) {
      console.error('Erro ao criar nota:', error);
      throw error;
    }
  }

  /**
   * Deleta uma nota
   */
  async deleteNote(noteId: number): Promise<any> {
    try {
      const response = await httpInterceptor.delete(
        `/pre-register/comments/${noteId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao deletar nota');
      }

      return data;
    } catch (error) {
      console.error('Erro ao deletar nota:', error);
      throw error;
    }
  }

  /**
   * Atualiza um atributo específico do pré-cadastro
   */
  async updatePreCadastroAttribute(
    preCadastroId: number,
    attributeName: string,
    value: any
  ): Promise<any> {
    try {
      const response = await httpInterceptor.patch(
        `/pre-register/attribute/${preCadastroId}`,
        { attributeName, value },
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao atualizar atributo');
      }

      return data;
    } catch (error) {
      console.error('Erro ao atualizar atributo:', error);
      throw error;
    }
  }

  /**
   * Envia mensagem para fila de WhatsApp e remove da fila
   */
  async sendWhatsAppQueue(phoneNumber: string): Promise<any> {
    try {
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const response = await httpInterceptor.delete(
        `/pre-register/whatsapp-queue/${cleanPhone}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao enviar mensagem WhatsApp');
      }

      return data;
    } catch (error) {
      console.error('Erro ao enviar mensagem WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Obtém status da fila WhatsApp para um pré-cadastro
   */
  async getWhatsAppQueue(preRegisterId: number): Promise<WhatsAppQueue[]> {
    try {
      const response = await httpInterceptor.get(
        `/pre-register/whatsapp-queue/${preRegisterId}`,
        {
          headers: this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar fila WhatsApp');
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar fila WhatsApp:', error);
      return [];
    }
  }

  /**
   * Obtém critérios de pontuação
   */
  async getScoreCriteria(): Promise<any> {
    try {
      const response = await httpInterceptor.get(
        '/pre-register/score-criteria',
        { headers: this.getHeaders() }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar critérios de pontuação');
      }
      return data;
    } catch (error) {
      console.error('Erro ao buscar critérios de pontuação:', error);
      throw error;
    }
  }

  /**
   * Atualiza pontos de um critério de pontuação
   */
  async updateScoreCriterio(id: number, pontos: number): Promise<any> {
    try {
      const response = await httpInterceptor.patch(
        `/pre-register/score-criteria/${id}`,
        { pontos },
        { headers: this.getHeaders() }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao atualizar pontuação');
      }
      return data;
    } catch (error) {
      console.error('Erro ao atualizar pontuação:', error);
      throw error;
    }
  }
}

export const managementService = new ManagementService();
